import { useState, useEffect } from 'react';
import { fetchItemDetails, searchTvShow } from '../../services/tmdbService';
import { searchYouTubeTrailer } from '../../services/youtubeService';
import styles from './DetailPage.module.css';

export default function DetailPage({ item, apiKey, ytKey, onBack }) {
  const [details, setDetails] = useState(null);
  const [trailer, setTrailer] = useState(item.fromYoutube ? {key: item.youtubeId, name: item.title} : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item.fromYoutube || item.suppressTrailer || !apiKey) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (item.fromApi && item.id) {
          const result = await fetchItemDetails(apiKey, item.id, item.mediaType || 'tv');
          setDetails(result.details);
          setTrailer(result.trailer || (ytKey ? await searchYouTubeTrailer(ytKey, item.title, item.titleEn) : null));
        } else if (!item.fromApi) {
          const found = await searchTvShow(apiKey, item.title);
          if (found) {
            const result = await fetchItemDetails(apiKey, found.id, 'tv');
            setTrailer(result.trailer || (ytKey ? await searchYouTubeTrailer(ytKey, item.title, item.titleEn) : null));
          } else if (ytKey) {
            setTrailer(await searchYouTubeTrailer(ytKey, item.title, item.titleEn));
          }
        }
      } catch {
        if (item.fromApi) setError('Could not load details from TMDB.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [item, apiKey]);

  const backdropUrl = details?.backdrop_path
    ? `https://image.tmdb.org/t/p/w1280${details.backdrop_path}`
    : item.fromYoutube ? item.poster : null;

  const title = details?.name || details?.title || item.title;
  const titleEn = details?.original_name || details?.original_title || item.titleEn || '';
  const fullDescription = details?.overview || item.description || '';
  const genres = details?.genres?.map(g => g.name) || (item.genre ? [item.genre] : []);
  const cast = details?.credits?.cast?.slice(0, 8) || [];
  const airDate = details?.first_air_date || details?.release_date || '';
  const seasons = details?.number_of_seasons;
  const episodes = details?.number_of_episodes;
  const runtime = details?.runtime;
  const networks = details?.networks?.map(n => n.name).join(', ') || item.channel || '';
  const rating = details?.vote_average
    ? details.vote_average.toFixed(1)
    : item.rating;
  const votes = details?.vote_count ?? item.votes;

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={onBack}>
        ← Back
      </button>

      <div className={styles.backdropWrap}>
        {backdropUrl
          ? <img src={backdropUrl} alt={title} className={styles.backdropImg} />
          : <div className={styles.backdropPlaceholder}><span>🌙</span></div>}
        <div className={styles.backdropFade} />
      </div>

      <div className={styles.content}>
        <div className={styles.hero}>
          {item.poster && (
            <img src={item.poster} alt={title} className={styles.poster} />
          )}
          <div className={styles.heroInfo}>
            <h1 className={styles.title}>{title}</h1>
            {titleEn && titleEn !== title && (
              <p className={styles.titleEn}>{titleEn}</p>
            )}
            {rating && (
              <div className={styles.rating}>
                ⭐ {rating}
                {votes > 0 && (
                  <span className={styles.votes}> ({votes.toLocaleString()} votes)</span>
                )}
              </div>
            )}
            <div className={styles.genres}>
              {genres.map(g => (
                <span key={g} className={styles.genre}>{g}</span>
              ))}
            </div>
            <div className={styles.metaList}>
              {airDate && <div className={styles.meta}>📅 {airDate}</div>}
              {seasons != null && (
                <div className={styles.meta}>
                  📺 {seasons} season{seasons !== 1 ? 's' : ''}
                  {episodes ? ` · ${episodes} episodes` : ''}
                </div>
              )}
              {runtime != null && (
                <div className={styles.meta}>⏱ {runtime} min</div>
              )}
              {networks && <div className={styles.meta}>📡 {networks}</div>}
              {item.stars && !networks && (
                <div className={styles.meta}>👤 {item.stars}</div>
              )}
            </div>
          </div>
        </div>

        {loading && (
          <div className={styles.loading}>🌙 Loading details...</div>
        )}
        {error && <div className={styles.error}>⚠️ {error}</div>}

        {fullDescription && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>About</h2>
            <p className={styles.description}>{fullDescription}</p>
          </section>
        )}

        {trailer && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Trailer</h2>
            <div className={styles.trailerWrap}>
              <iframe
                className={styles.trailer}
                src={`https://www.youtube.com/embed/${trailer.key}`}
                title={trailer.name}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </section>
        )}

        {cast.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Cast</h2>
            <div className={styles.cast}>
              {cast.map(actor => (
                <div key={actor.id} className={styles.actor}>
                  {actor.profile_path ? (
                    <img
                      src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`}
                      alt={actor.name}
                      className={styles.actorImg}
                    />
                  ) : (
                    <div className={styles.actorPh}>👤</div>
                  )}
                  <div className={styles.actorName}>{actor.name}</div>
                  {actor.character && (
                    <div className={styles.actorChar}>{actor.character}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {item.note && (
          <section className={styles.section}>
            <div className={styles.note}>💡 {item.note}</div>
          </section>
        )}
      </div>
    </div>
  );
}
